/*
 * Frontend API wrapper used by the CRA client.
 *
 * Response-shape normalization lives here so page components can stay focused
 * on UI state instead of repetitive fetch/error handling.
 */
/**
 * This class contains static functions that send data
 * to the backend api
 */
export default class APIService {
    /**
     * Sends data to '/api/v1/accept_academic_integrity_disclaimer' endpoint.
     * @param setOpenModal Sets the state of the academic integrity disclaimer modal.
     * @returns {Promise<any>} response from python endpoint
     */
    static AcceptAcademicIntegrityDisclaimer(setOpenModal) {
        return fetch('/api/v1/accept-academic-integrity-disclaimer', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: "include",
        })
            .then(response => response.json())
            .then(data => {
                if (!data.openModal) {
                    setOpenModal(false);
                }
            })
            .catch(error => console.error(error))
    }

    /**
     * Sends data to '/api/v1/open_academic_integrity_disclaimer' endpoint.
     * @param setOpenModal Sets the state of the academic integrity disclaimer modal.
     * @returns {Promise<any>} response from python endpoint
     */
    static OpenAcademicIntegrityDisclaimer(setOpenModal) {
        return fetch('/api/v1/open-academic-integrity-disclaimer', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: "include",
        })
            .then(response => response.json())
            .then(data => {
                setOpenModal(data.openModal);
            })
            .catch(error => console.error(error))
    }


  /**
   * Sends data to `/api/v1/user-prompt` endpoint.
   * @param userInput Student's question to the tutoring system
   * @param chatSessionId The chat_session_id to continue the conversation
   * @returns {Promise<any>} response from python endpoint
   */
  static InsertPrompt(userInput, chatSessionId, courseId, assignmentId, assignmentName) {
    return fetch("/api/v1/user-prompt", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ 
        value: userInput,
        chat_session_id: chatSessionId,
        course_id: courseId,
        assignment_id: assignmentId,
        assignment_name: assignmentName,
      }),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || "Failed to generate response");
        }
        return data;
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  }

  static InsertFeedback(feedbackText) {
    return fetch("/api/v1/send-user-feedback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        feedback_text: feedbackText
      }),
    })
      .then((response) => response.json())
      .catch((error) => {
        console.error(error);
        throw error;
      });
  }

  static Logout() {
    // logout endpoint redirects user, it does not return a value
    window.location.href = "/api/v1/logout";
  }

  static InsertStudentData(formData) {
    return fetch("/api/v1/record-student-data", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .catch((error) => {
        console.error(error);
        throw error;
      });
  }

  static GetStudentData() {
    return fetch("/api/v1/check-student-data", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then((response) => response.json())
      .catch((error) => {
        console.error(error);
        throw error;
      });
  }

  /**
   * Creates a new course in the backend
   * @param {Object} courseData - { courseId, name, description, professorId }
   * @returns {Promise<any>} response from python endpoint
   */
  static CreateCourse(courseData) {
    return fetch("/api/v1/create-course", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(courseData),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || data?.error || "Failed to create course");
        }
        return data;
      })
      .catch((error) => {
        console.error("Error creating course:", error);
        throw error;
      });
  }
  
  static SelectCourse(courseId) {
    return fetch("/api/v1/select-course", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ course_id: courseId }),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || "Failed to select course");
        }
        return data;
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  }

  /**
   * Fetches messages for a specific chat session
   * @param chatSessionId The chat session ID to fetch messages for
   * @returns {Promise<Array>} Array of messages with type and text
   */
  static GetChatMessages(chatSessionId) {
    return fetch(`/api/v1/chat-messages/${chatSessionId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        const messages = [];
        if (Array.isArray(data.messages)) {
          data.messages.forEach(msg => {
            if (msg.role === "user" && msg.content) {
              messages.push({ type: "user", text: msg.content, createdAt: msg.createdAt });
            }
            if (msg.role === "assistant" && msg.content) {
              messages.push({
                type: "bot",
                text: msg.content,
                metadata: msg.metadata || null,
                createdAt: msg.createdAt,
              });
            }
          });
        }
        return messages;
      })
      .catch((error) => {
        console.error(error);
        return [];
      });
  }

  static GetStudentChats() {
    return fetch("/api/v1/chats", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || "Failed to fetch sessions");
        }
        return data;
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  }

  static GetCurrentUser() {
    return fetch("/api/v1/get-current-user", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then((response) => response.json())
      .catch((error) => console.error(error));
  }

  static GetUserPrimaryAttribute() {
  let url = "/api/v1/get-user-primary-attribute";

  return fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  })
    .then((response) => response.json())
    .catch((error) => console.error(error));
}

  /**
   * Uploads one or more syllabus files to the backend for ingestion.
   *
   * Sends a multipart/form-data POST request. The browser sets the
   * Content-Type header automatically (including the boundary), so we
   * must NOT set it manually here.
   *
   * @param {FileList|File[]} files - Files selected by the manager
   * @returns {Promise<{
   *   message: string,
   *   total: number,
   *   success: number,
   *   failed: number,
   *   files: Array<{filename: string, status: string, reason?: string, courseId?: string}>
   * }>}
   */
  static UploadSyllabus(files, metadata = {}) {
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append("files", file));
    if (metadata.courseId) {
      formData.append("course_id", metadata.courseId);
    }
    if (metadata.assignmentId) {
      formData.append("assignment_id", metadata.assignmentId);
    }
    if (metadata.documentType) {
      formData.append("document_type", metadata.documentType);
    }
    if (metadata.replacementFilename) {
      formData.append("replacement_filename", metadata.replacementFilename);
    }

    return fetch("/api/v1/upload-syllabus", {
      method: "POST",
      body: formData,
      // Do NOT set Content-Type – the browser will set multipart/form-data
      // with the correct boundary automatically.
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((err) => {
            throw new Error(err.error || "Upload failed.");
          });
        }
        return response.json();
      })
      .catch((error) => {
        console.error("Error uploading syllabus:", error);
        throw error;
      });
  }

  static GetAssignments(courseId, includeInactive = false) {
    return fetch(
      `/api/v1/courses/${encodeURIComponent(courseId)}/assignments${includeInactive ? "?include_inactive=1" : ""}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      }
    )
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || "Failed to fetch assignments");
        }
        return Array.isArray(data.assignments) ? data.assignments : [];
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  }

  static CreateAssignment(courseId, assignmentData) {
    return fetch(`/api/v1/courses/${encodeURIComponent(courseId)}/assignments`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(assignmentData),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || data?.error || "Failed to create assignment");
        }
        return data.assignment;
      });
  }

  static UpdateAssignment(assignmentId, assignmentData) {
    return fetch(`/api/v1/assignments/${assignmentId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(assignmentData),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || "Failed to update assignment");
        }
        return data.assignment;
      });
  }

  static DeleteAssignment(assignmentId) {
    return fetch(`/api/v1/assignments/${assignmentId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || "Failed to archive assignment");
        }
        return data;
      });
  }

  static GetFacultyInsights(filters = {}) {
    const params = new URLSearchParams();
    if (filters.course) params.set("course", filters.course);
    if (filters.assignment) params.set("assignment", filters.assignment);
    if (filters.student) params.set("student", filters.student);
    if (filters.category) params.set("category", filters.category);
    if (filters.dateFrom) params.set("date_from", filters.dateFrom);
    if (filters.dateTo) params.set("date_to", filters.dateTo);
    if (filters.includeLegacy) params.set("include_legacy", "1");
    const suffix = params.toString() ? `?${params.toString()}` : "";

    return fetch(`/api/v1/faculty-insights${suffix}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          const error = new Error(data?.message || "Failed to load faculty insights");
          error.status = response.status;
          throw error;
        }
        return data;
      });
  }

  static GetFacultySessionMessages(sessionId) {
    return fetch(`/api/v1/faculty-sessions/${sessionId}/messages`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          const error = new Error(data?.message || "Failed to load session transcript");
          error.status = response.status;
          throw error;
        }

        const messages = [];
        if (Array.isArray(data.messages)) {
          data.messages.forEach((msg) => {
            if (msg.role === "user" && msg.content) {
              messages.push({ type: "user", text: msg.content, createdAt: msg.createdAt });
            }
            if (msg.role === "assistant" && msg.content) {
              messages.push({
                type: "assistant",
                text: msg.content,
                metadata: msg.metadata || null,
                createdAt: msg.createdAt,
              });
            }
          });
        }
        return messages;
      });
  }

  static GetCourses(includeInactive = false, includeDetails = false) {
    const params = new URLSearchParams();
    if (includeInactive) params.set("include_inactive", "1");
    if (includeDetails) params.set("include_details", "1");
    const suffix = params.toString() ? `?${params.toString()}` : "";

    return fetch(`/api/v1/get-courses${suffix}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || data?.error || "Failed to load courses");
        }
        return data;
      });
  }

  static UpdateCourse(courseId, courseData) {
    return fetch(`/api/v1/courses/${encodeURIComponent(courseId)}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(courseData),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || data?.error || "Failed to update course");
        }
        return data.course;
      });
  }

  static GetCourseOwnershipAudit() {
    return fetch("/api/v1/course-ownership-audit", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || data?.error || "Failed to load course ownership audit");
        }
        return data;
      });
  }

  static ReassignCourseOwner(courseId, payload) {
    return fetch(`/api/v1/courses/${encodeURIComponent(courseId)}/owner-assignment`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || data?.error || "Failed to reassign course owner");
        }
        return data;
      });
  }

  static DeleteCourse(courseId, hardDelete = false) {
    const suffix = hardDelete ? "?hard=1" : "";
    return fetch(`/api/v1/courses/${encodeURIComponent(courseId)}${suffix}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || data?.error || "Failed to delete course");
        }
        return data;
      });
  }

  static GetCourseResources(courseId, assignmentId = "", includeInactive = false) {
    const params = new URLSearchParams({ course_id: courseId });
    if (assignmentId !== "" && assignmentId !== null && assignmentId !== undefined) {
      params.set("assignment_id", assignmentId);
    }
    if (includeInactive) {
      params.set("include_inactive", "1");
    }

    return fetch(`/api/v1/course-resources?${params.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || data?.error || "Failed to load uploaded resources");
        }
        return Array.isArray(data.resources) ? data.resources : [];
      });
  }

  static DeleteCourseResource(resourceId) {
    return fetch(`/api/v1/course-resources/${resourceId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || data?.error || "Failed to delete file");
        }
        return data;
      });
  }

  static UpdateCourseResource(resourceId, resourceData) {
    return fetch(`/api/v1/course-resources/${resourceId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(resourceData),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || data?.error || "Failed to update file");
        }
        return data.resource;
      });
  }

}
